// src/components/dashboard/EntitlementCard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  EntitlementGrantStatus,
  EntitlementType,
} from '../../models/entitlement/EntitlementSummary';
import type { Membership } from '../../models/Membership';

interface EntitlementCardProps {
  membership: Membership | null;
}

/** Format a calendar-date ISO string (e.g. '2026-09-18T00:00:00' or '2026-09-18')
 *  as a locale date string WITHOUT any UTC coercion, so the displayed date always
 *  matches the calendar date the backend intended regardless of the user's timezone. */
function formatExpiryDate(iso: string): string {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return iso;
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

const EntitlementCard: React.FC<EntitlementCardProps> = ({ membership }) => {
  const ent = membership?.entitlement;
  if (!ent || ent.status !== EntitlementGrantStatus.Active) return null;

  const hours = ent.dailyMinutesAllowance / 60;
  const hoursLabel = `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hrs`;

  // Default to Pass when the backend omits `type` (older responses) — see model note.
  const isMembership = ent.type === EntitlementType.Membership;
  const badgeLabel = isMembership ? 'Membership' : 'Season Pass';

  const expiryValue = ent.expiresAt
    ? formatExpiryDate(ent.expiresAt)
    : 'Activates on first use';

  return (
    <div className="entitlement-card">
      <div className="ent-stripe" aria-hidden="true" />
      <div className="ent-body">
        <span className="ent-badge">
          {!isMembership && <span aria-hidden="true">★ </span>}
          {badgeLabel}
        </span>
        <h2 className="ent-name">{membership.name}</h2>
        <div className="ent-stats">
          <div className="ent-stat">
            <div className="ent-stat-key">Daily allowance</div>
            <div className="ent-stat-value">{hoursLabel}</div>
          </div>
          <div className="ent-stat">
            <div className="ent-stat-key">Valid until</div>
            <div className="ent-stat-value ent-stat-value--plain">{expiryValue}</div>
          </div>
        </div>
        <Link to="/booking/entitlement" className="ent-cta">
          {isMembership ? 'Book with your membership' : 'Book with your pass'}
        </Link>
      </div>
    </div>
  );
};

export default EntitlementCard;
